import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/use-toast';
import { contentGalleryRepository } from '../repositories/contentGallery.repository';
import { contentKeys } from '../services/content.keys';
import type { ContentFolder } from '../types/content.types';

export const useContentFolders = (campaignId: string) => {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id || '';

  return useQuery({
    queryKey: contentKeys.folders(orgId, campaignId),
    queryFn: async (): Promise<ContentFolder[]> => {
      if (!campaignId || !orgId) return [];
      return contentGalleryRepository.getFolders(orgId, campaignId);
    },
    enabled: !!campaignId && !!orgId,
  });
};

export const useCreateContentFolder = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      campaignId,
      name,
      organizationId,
    }: {
      campaignId: string;
      name: string;
      organizationId?: string;
    }) => {
      const orgId = organizationId || currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      return contentGalleryRepository.createFolder(orgId, campaignId, name);
    },
    onSuccess: (_, variables) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: contentKeys.folders(orgId, variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyFolders(variables.campaignId) });
      toast({
        title: 'Success',
        description: 'Folder created successfully',
      });
    },
    onError: (error) => {
      console.error('Error creating folder:', error);
      toast({
        title: 'Error',
        description: 'Failed to create folder',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteContentFolder = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      folderId,
      campaignId,
    }: {
      folderId: string;
      campaignId: string;
    }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await contentGalleryRepository.deleteFolder(orgId, folderId);
      return { folderId, campaignId };
    },
    onSuccess: (_, variables) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: contentKeys.folders(orgId, variables.campaignId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyFolders(variables.campaignId) });
      toast({
        title: 'Success',
        description: 'Folder deleted successfully',
      });
    },
    onError: (error) => {
      console.error('Error deleting folder:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete folder',
        variant: 'destructive',
      });
    },
  });
};
